import { ITask, TaskActionOptions } from '@modules/TasksModule/types';
import TasksModule from '@modules/TasksModule';
import InboxModule from '..';
import { InboxContext } from '../types';
import { inboxActions, inboxState } from '../state';
import { setCallToCompleted } from '../callHandlers/callStateHandler';
import { applyDeferredSelection } from '../lib/applyDeferredSelection';
import { selectReturnCard } from '../lib/selectReturnCard';

/**
 * The return card of a Close whose task is still open after completion ran
 * (the classification form is open), keyed by the task's view key. The form's
 * submit completes the task with no options, so it is read back from here.
 */
const pendingReturnViewKeys = new Map<string, string>();

/**
 * Retention on: the restore waits for the completion result, so a blocked
 * Close or an open classification form keeps the clicked card selected.
 * Runs in a `finally` so a throw after the task was completed still settles
 * selection.
 */
async function completeThenSettleSelection(
  context: InboxContext,
  task: ITask,
  classified: boolean,
  options?: TaskActionOptions,
) {
  const viewKey = task.viewKey;
  const returnViewKey =
    options?.returnViewKey ??
    (classified && viewKey ? pendingReturnViewKeys.get(viewKey) : undefined);
  try {
    await setCallToCompleted(
      context,
      task.id,
      classified,
      options?.classificationRequirement,
    );
  } catch (error) {
    context.logger.error('Completing the task failed', error);
  } finally {
    // the task keeps its (possibly re-keyed) id on the same object
    if (!TasksModule.getTask(task.id)) {
      if (inboxState.selectedTaskId === task.id) {
        selectReturnCard(returnViewKey);
      }
      // previousSelectedTaskId never names a completed task
      inboxActions.clearPreviousSelectedTaskId(task.id);
      if (viewKey) {
        pendingReturnViewKeys.delete(viewKey);
      }
    } else if (viewKey && !classified) {
      // each Close replaces the last one's entry, even when it has none
      if (options?.returnViewKey) {
        pendingReturnViewKeys.set(viewKey, options.returnViewKey);
      } else {
        pendingReturnViewKeys.delete(viewKey);
      }
    }
    applyDeferredSelection(options?.deferredSelection);
  }
}

function handleTaskComplete(
  context: InboxContext,
  task: ITask,
  classified: boolean,
  options?: TaskActionOptions,
): void {
  if (InboxModule.isBookingRetentionEnabled()) {
    void completeThenSettleSelection(context, task, classified, options);
    return;
  }

  inboxActions.restorePrevSelectedTaskId();

  setCallToCompleted(context, task.id, classified);
}

export default handleTaskComplete;
